import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { User, IdentificationCard, Bank, Phone, Camera } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Phase 1 — Staff Management. Sectioned profile dialog used for both Add and
// Edit. Captures everything beyond the basic email/password/role triad: photo,
// designation, branch, joining date, reporting manager, salary type +
// monthly_salary, bank details, KYC (Aadhaar/PAN), emergency contact, login
// permission. All fields except Name / Email / Password (for Add) are optional.

const EMPTY = {
  name: '', email: '', password: '', role: 'staff',
  mobile: '', address: '',
  photo: '',
  designation: '', branch: '', joining_date: '',
  reporting_manager_id: '',
  salary_type: 'monthly', monthly_salary: '',
  bank_account_name: '', bank_account_number: '', bank_ifsc: '',
  aadhaar_number: '', pan_number: '',
  emergency_contact_name: '', emergency_contact_phone: '',
  active: true, login_enabled: true,
};

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 mt-1 mb-2 pt-3 border-t border-[#F4F4F5] first:border-t-0 first:pt-0">
    <Icon size={14} className="text-[#3B82F6]" />
    <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">{children}</p>
  </div>
);

const Field = ({ label, children, span = 1 }) => (
  <div className={span === 2 ? 'col-span-2' : ''}>
    <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

export default function StaffProfileDialog({ open, onOpenChange, target, managers, onSaved }) {
  const isEdit = !!target;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (target) {
        setForm({
          ...EMPTY,
          ...target,
          password: '',
          monthly_salary: target.monthly_salary != null ? String(target.monthly_salary) : '',
        });
      } else {
        setForm(EMPTY);
      }
    }
  }, [open, target]);

  const onPhotoFile = (file) => {
    if (!file) return;
    if (file.size > 800 * 1024) { toast.error('Photo too large (max 800KB)'); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(prev => ({ ...prev, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!form.name?.trim() || !form.email?.trim()) {
      toast.error('Name and email are required'); return;
    }
    if (!isEdit && !form.password?.trim()) {
      toast.error('Password required for new staff'); return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Only send password if provided (edit case keeps current if blank).
      if (!payload.password) delete payload.password;
      // Coerce monthly_salary to a number (or 0 if blank).
      payload.monthly_salary = parseFloat(payload.monthly_salary) || 0;
      await onSaved(payload, isEdit);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl border-[#E4E4E7] max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Staff' : 'Add Staff Member'}</DialogTitle>
          <DialogDescription>{isEdit ? `Update ${target?.name || ''}'s profile.` : 'Create a new staff account with full profile.'}</DialogDescription>
        </DialogHeader>

        {/* Photo + Basic */}
        <SectionTitle icon={User}>Basic</SectionTitle>
        <div className="flex gap-4 items-start">
          <div className="flex-shrink-0">
            <div className="w-20 h-20 rounded-xl border-2 border-dashed border-[#E4E4E7] bg-[#FAFAFA] overflow-hidden flex items-center justify-center relative group">
              {form.photo ? (
                <img src={form.photo} alt="staff" className="w-full h-full object-cover" />
              ) : (
                <Camera size={22} className="text-[#A1A1AA]" />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={e => onPhotoFile(e.target.files?.[0])}
                className="absolute inset-0 opacity-0 cursor-pointer"
                data-testid="staff-photo-input"
              />
            </div>
            {form.photo && (
              <button type="button" onClick={() => setForm({ ...form, photo: '' })} className="text-[10px] text-[#EF4444] mt-1 hover:underline">
                Remove
              </button>
            )}
          </div>
          <div className="flex-1 grid grid-cols-2 gap-3">
            <Field label="Full Name *">
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-lg" data-testid="profile-name" />
            </Field>
            <Field label="Email *">
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded-lg" data-testid="profile-email" />
            </Field>
            <Field label="Mobile">
              <Input value={form.mobile} onChange={e => setForm({ ...form, mobile: e.target.value })} className="rounded-lg" data-testid="profile-mobile" />
            </Field>
            <Field label={isEdit ? 'New Password (leave blank to keep)' : 'Password *'}>
              <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} className="rounded-lg" data-testid="profile-password" />
            </Field>
            <Field label="Address" span={2}>
              <Textarea rows={2} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="rounded-lg" data-testid="profile-address" />
            </Field>
          </div>
        </div>

        {/* Job */}
        <SectionTitle icon={IdentificationCard}>Job</SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Designation">
            <Input value={form.designation} onChange={e => setForm({ ...form, designation: e.target.value })} className="rounded-lg" data-testid="profile-designation" />
          </Field>
          <Field label="Branch">
            <Input value={form.branch} onChange={e => setForm({ ...form, branch: e.target.value })} className="rounded-lg" data-testid="profile-branch" />
          </Field>
          <Field label="Joining Date">
            <Input type="date" value={form.joining_date} onChange={e => setForm({ ...form, joining_date: e.target.value })} className="rounded-lg" data-testid="profile-joining-date" />
          </Field>
          <Field label="Reporting Manager">
            <Select value={form.reporting_manager_id || ''} onValueChange={v => setForm({ ...form, reporting_manager_id: v === '__none__' ? '' : v })}>
              <SelectTrigger className="rounded-lg" data-testid="profile-manager"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {(managers || []).filter(m => m.id !== target?.id).map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Role">
            <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
              <SelectTrigger className="rounded-lg" data-testid="profile-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Login Permission">
            <Select value={form.login_enabled ? 'yes' : 'no'} onValueChange={v => setForm({ ...form, login_enabled: v === 'yes' })}>
              <SelectTrigger className="rounded-lg" data-testid="profile-login-enabled"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Allowed</SelectItem>
                <SelectItem value="no">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </div>

        {/* Salary */}
        <SectionTitle icon={Bank}>Salary &amp; Bank</SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Salary Type">
            <Select value={form.salary_type || 'monthly'} onValueChange={v => setForm({ ...form, salary_type: v })}>
              <SelectTrigger className="rounded-lg" data-testid="profile-salary-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="daily">Daily Wage</SelectItem>
                <SelectItem value="commission">Commission Based</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Monthly Salary (₹)">
            <Input type="number" min={0} value={form.monthly_salary} onChange={e => setForm({ ...form, monthly_salary: e.target.value })} className="rounded-lg" data-testid="profile-monthly-salary" />
          </Field>
          <Field label="Bank Account Name">
            <Input value={form.bank_account_name} onChange={e => setForm({ ...form, bank_account_name: e.target.value })} className="rounded-lg" data-testid="profile-bank-name" />
          </Field>
          <Field label="Bank Account Number">
            <Input value={form.bank_account_number} onChange={e => setForm({ ...form, bank_account_number: e.target.value })} className="rounded-lg" data-testid="profile-bank-acc" />
          </Field>
          <Field label="IFSC">
            <Input value={form.bank_ifsc} onChange={e => setForm({ ...form, bank_ifsc: e.target.value.toUpperCase() })} className="rounded-lg" data-testid="profile-ifsc" />
          </Field>
          <Field label="Aadhaar Number">
            <Input value={form.aadhaar_number} onChange={e => setForm({ ...form, aadhaar_number: e.target.value })} className="rounded-lg" data-testid="profile-aadhaar" />
          </Field>
          <Field label="PAN Number">
            <Input value={form.pan_number} onChange={e => setForm({ ...form, pan_number: e.target.value.toUpperCase() })} className="rounded-lg" data-testid="profile-pan" />
          </Field>
        </div>

        {/* Emergency */}
        <SectionTitle icon={Phone}>Emergency Contact</SectionTitle>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact Name">
            <Input value={form.emergency_contact_name} onChange={e => setForm({ ...form, emergency_contact_name: e.target.value })} className="rounded-lg" data-testid="profile-emerg-name" />
          </Field>
          <Field label="Contact Phone">
            <Input value={form.emergency_contact_phone} onChange={e => setForm({ ...form, emergency_contact_phone: e.target.value })} className="rounded-lg" data-testid="profile-emerg-phone" />
          </Field>
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="profile-save-btn">
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Staff Member'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
